import React, { useState, useMemo } from "react";
import { Box, Snackbar, Alert } from "@mui/material";
import { useQuery } from "react-query";

// Subcomponents
import EventHeader from "./components/EventHeader";
import BookingsTable from "./components/BookingsTable";
import ManageMaterialsModal from "./components/ManageMaterialsModal";
import SocialPosterModal from "./components/SocialPosterModal";
import FilePreviewLightbox from "./components/FilePreviewLightbox";

// Services & Hooks
import { usePrerequisitesRequests } from "../../../hooks/usePrerequisitesRequests";
import { fetchDesignerSocialMediaUploadRequests } from "../../../services/eventUploadsService";

const UpcomingEvents = ({
  defaultStatus = "upcoming",
  userRole = "designer",
  eventType = "FDP"
}) => {
  const isOngoingOrPast =
    defaultStatus === "ongoing" ||
    defaultStatus === "past" ||
    defaultStatus === "completed";

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Debounce search input by 400ms
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when eventType or status changes
  React.useEffect(() => {
    setPage(0);
  }, [eventType, defaultStatus]);

  // ── 1. React Query for Upcoming Prerequisites Requests ─────────────────
  const {
    data: upcomingResponse,
    isLoading: isUpcomingLoading,
    isFetching: isUpcomingFetching,
    refetch: refetchUpcoming
  } = usePrerequisitesRequests(
    {
      eventType,
      search: debouncedSearch,
      page: page + 1,
      page_size: rowsPerPage
    },
    { enabled: !isOngoingOrPast },
    userRole
  );

  // ── 2. React Query for Ongoing & Completed Social Media Upload Requests ─
  const {
    data: socialMediaResponse,
    isLoading: isSocialLoading,
    isFetching: isSocialFetching,
    refetch: refetchSocial
  } = useQuery(
    ["designer-social-media-upload-requests", debouncedSearch, page, rowsPerPage],
    () =>
      fetchDesignerSocialMediaUploadRequests({
        search: debouncedSearch,
        page: page + 1,
        page_size: rowsPerPage
      }),
    {
      enabled: isOngoingOrPast,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      keepPreviousData: true
    }
  );

  const activeResponse = isOngoingOrPast ? socialMediaResponse : upcomingResponse;
  const rawEvents = Array.isArray(activeResponse?.data)
    ? activeResponse.data
    : Array.isArray(activeResponse)
    ? activeResponse
    : [];
  const totalCount = activeResponse?.total ?? rawEvents.length;

  const isLoading = isOngoingOrPast ? isSocialLoading : isUpcomingLoading;
  const isFetching = isOngoingOrPast ? isSocialFetching : isUpcomingFetching;
  const refetch = isOngoingOrPast ? refetchSocial : refetchUpcoming;

  // Map events to ensure consistent timelineStatus
  const events = useMemo(() => {
    return rawEvents.map((evt) => ({
      ...evt,
      timelineStatus: defaultStatus
    }));
  }, [rawEvents, defaultStatus]);

  // Selected event for popup modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isSocialPosterModalOpen, setIsSocialPosterModalOpen] = useState(false);

  // File Preview Lightbox state
  const [previewFile, setPreviewFile] = useState(null);

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  // Open Manage Materials / Geo Photos Modal
  const handleOpenManageModal = (event) => {
    setSelectedEvent(event);
    setIsManageModalOpen(true);
  };

  // Open Social Poster Upload / Review Modal
  const handleOpenSocialPosterModal = (event) => {
    setSelectedEvent(event);
    setIsSocialPosterModalOpen(true);
  };

  // Update event data handler (refetches query to stay synced with backend)
  const handleUpdateEvent = (updatedEvent) => {
    setSelectedEvent(updatedEvent);
    refetch();
    setToast({
      open: true,
      message: `Updated details for ${updatedEvent.institute_name || updatedEvent.instituteName || "Event"}!`,
      severity: "success"
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f4f7fb",
        minHeight: "100vh",
        p: { xs: 2, sm: 2.5, md: 3 },
        fontFamily: "'Inter', sans-serif"
      }}
    >
      {/* Header with Search */}
      <EventHeader
        timelineStatus={defaultStatus}
        fixedEventType={userRole === "admin" ? eventType : null}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onRefresh={refetch}
        isRefreshing={isFetching && !isLoading}
      />

      {/* Main Tabular View */}
      <BookingsTable
        events={events}
        isLoading={isLoading}
        onOpenManageModal={handleOpenManageModal}
        onOpenSocialPosterModal={handleOpenSocialPosterModal}
        onPreviewFile={(file) => setPreviewFile(file)}
        userRole={userRole}
        timelineStatus={defaultStatus}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      {/* Interactive Modal to Upload, View, Edit, Delete, Accept/Reject Prerequisites & Geo Photos */}
      {isManageModalOpen && selectedEvent && (
        <ManageMaterialsModal
          isOpen={isManageModalOpen}
          onClose={() => {
            setIsManageModalOpen(false);
            setSelectedEvent(null);
          }}
          event={selectedEvent}
          onUpdateEvent={handleUpdateEvent}
          onPreviewFile={(file) => setPreviewFile(file)}
          userRole={userRole}
        />
      )}

      {/* Social Poster Upload & Review Modal */}
      {isSocialPosterModalOpen && selectedEvent && (
        <SocialPosterModal
          isOpen={isSocialPosterModalOpen}
          onClose={() => {
            setIsSocialPosterModalOpen(false);
            setSelectedEvent(null);
          }}
          event={selectedEvent}
          onUpdateEvent={handleUpdateEvent}
          onPreviewFile={(file) => setPreviewFile(file)}
          userRole={userRole}
        />
      )}

      {/* Full Resolution Lightbox File Preview */}
      <FilePreviewLightbox
        previewFile={previewFile}
        onClose={() => setPreviewFile(null)}
        onRemoveFile={() => setPreviewFile(null)}
        userRole={userRole}
      />

      {/* Toast feedback */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{
            width: "100%",
            borderRadius: "8px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
            fontWeight: 500,
            fontSize: "0.8125rem"
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UpcomingEvents;
